// externals
import React, { useEffect, useRef, useState } from 'react';
import { ChevronRightIcon } from '@interstate/components/Icons/ChevronRightIcon';
import { DocumentIcon } from '@interstate/components/Icons';
import type { InterstateOnChangeCallback } from '@interstate/components/InterstateEvents';
import type { SelectInputEventValue } from '@interstate/components/SelectInput';
import * as baseDealActionCreators from '@makemydeal/dr-dash-store/actions/baseDealActionCreators';
import * as baseDealAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/baseDealAnalyticsActionCreators';
import * as interactionAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/interactionAnalyticsActionCreators';
import * as lenderActionCreators from '@makemydeal/dr-dash-store/actions/lenderActionCreators';
import * as offerReduxActionCreators from '@makemydeal/dr-dash-store/actions/offerRedux';
import * as fullLenderDecisionsSelectors from '@makemydeal/dr-dash-store/selectors/fullLenderDecisionsSelectors';
import * as lenderListSelectors from '@makemydeal/dr-dash-store/selectors/lenderListSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import { getVehicleAnalyticsPayload } from '@makemydeal/dr-dash-store/selectors/vehicleSelectors';
import { ContractType } from '@makemydeal/dr-dash-types';
import type { LenderListState, SelectedLenderWithAddress, FldLenderDecision } from '@makemydeal/dr-dash-types';
import type { ErrorState } from '@makemydeal/dr-dash-ui-utils';
import type { LenderAddress } from '@makemydeal/dr-platform-types';
import { FINANCE, LEASE } from '@makemydeal/dr-platform-types';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { kebabCase } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import {
    CREDIT_DECISION_LENDER_NAME,
    CREDIT_DECISION_CONTRACTING_LENDER_NAME as lenderLabel,
    CREDIT_DECISION_LENDER_NAME_FIELD
} from '../../constants';
import { Dropdown } from '@makemydeal/dr-shared-components';
import type { DropdownOptionWithDescription } from '@makemydeal/dr-shared-components';
import { DropdownWithDescription } from '@makemydeal/dr-shared-components';
import { useIsLenderFieldDisabled } from '../../hooks';
import { appendHardcodedContractingLenders } from './hardcodedContractingLenders';
import {
    getValidLenderPhone,
    getValidLenderPostalCode,
    validateLendersListPhoneWithAreaCode
} from '@makemydeal/dr-shared-ui-utils/utils/lenderValidationUtils';
import {
    orderLendersByDecisionStatus,
    findLenderDecisionById,
    groupLendersByDecisionStatus,
    isDecisionStatus
} from '../../utils/lenderDecisionUtils';
import { DealTabs } from '@makemydeal/dr-shared-types';
import ActionWithErrorBadge from '../../header/ActionWithErrorBadge';
import { fetchLenderAddress } from '@makemydeal/dr-dash-store/actions/lenderActionCreators';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import ReserveSplitClearedAlert from './ReserveSplitClearedAlert';
import type { LenderChangeOrigin } from '@makemydeal/dr-offer-redux';

type CreditDecisionLenderNameFieldProps = {
    isSummaryTab?: boolean;
    isInLenderPanel?: boolean;
    onLenderSlideOutOpen?: () => void;
    onChangeContractFieldValidation?: (t: string) => void;
    error?: ErrorState;
};

const CreditDecisionLenderNameField = ({
    isSummaryTab,
    isInLenderPanel,
    onLenderSlideOutOpen,
    onChangeContractFieldValidation,
    error
}: CreditDecisionLenderNameFieldProps) => {
    const dispatch = useDispatch();
    const lender = useSelector(offerReduxSelectors.getCreditDecisionLender);
    const traceIdBefore = useSelector(offerReduxSelectors.getTraceId);
    const lenderList = useSelector(lenderListSelectors.getLenderList);
    const isFordLincolnLendersEnabled = useSelector(featureToggleSelectors.isFordLincolnLendersEnabled);
    const lenderDecisions = isFordLincolnLendersEnabled ? appendHardcodedContractingLenders(lenderList) : lenderList;
    const offerType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const isLenderDropdownDisabled = useIsLenderFieldDisabled();
    const vehicleAnalyticsPayload = useSelector(getVehicleAnalyticsPayload);

    const theme = useInterstateTheme();

    const isGCDynamicValidationsEnabled = useSelector(featureToggleSelectors.isGCDynamicValidationsEnabled);
    const isEnableLenderAddressF267947 = useSelector(featureToggleSelectors.isEnableLenderAddress);
    const enableLenderAddress = useSelector(featureToggleSelectors.enableLenderAddress);
    const isEnableMVLenderDecisions = useSelector(featureToggleSelectors.isEnableMVLenderDecisions);
    const isPaperLenderEnabled = useSelector(featureToggleSelectors.isPaperLenderEnabled);
    const isReserveSplitOverrideEnabled = useSelector(featureToggleSelectors.isReserveSplitOverrideEnabled);
    const isSelectedLenderPaper = useSelector(lenderListSelectors.isSelectedLenderPaperLender);
    const isFldFinanceMethodSameAsCurrentOfferType = useSelector(
        fullLenderDecisionsSelectors.isFldFinanceMethodSameAsCurrentOfferType
    );

    const allLenderDecisions = useSelector(fullLenderDecisionsSelectors.getFullLenderDecisionsList);
    const hasLenderDecisions = useSelector(fullLenderDecisionsSelectors.getHasFullLenderDecisions);
    const isRouteOneDealer = useSelector(dealerSelectors.isCreditProviderRouteOne);

    const hasFldDataForCurrentOfferType =
        isEnableMVLenderDecisions && hasLenderDecisions && isFldFinanceMethodSameAsCurrentOfferType;
    // R1 FLD lender IDs don't match DT lender list IDs — skip grouping and status sync
    const shouldGroupByDecisionStatus = hasFldDataForCurrentOfferType && !isRouteOneDealer;

    const orderedLenderList = shouldGroupByDecisionStatus
        ? orderLendersByDecisionStatus(lenderDecisions, allLenderDecisions)
        : lenderDecisions;

    const fieldWrapperRef = useRef<HTMLDivElement>(null);
    const [inputPaddingLeft, setInputPaddingLeft] = useState<string>();

    const selectedValue = lender?.id || '';
    const selectedLenderName = lender?.name || '';
    const selectedLenderIdForGrouping =
        selectedValue || (lender?.name ? lenderDecisions.find((item) => item.lenderName === lender.name)?.lenderId || '' : '');

    const options = mapLenderToOption({
        lendersDecision: orderedLenderList,
        isEnableLenderAddress: isEnableLenderAddressF267947,
        isGroupedByDecisionStatus: shouldGroupByDecisionStatus,
        allLenderDecisions,
        selectedLenderId: selectedLenderIdForGrouping,
        selectedLenderName,
        isPaperLenderEnabled
    });

    const handleClick = () => {
        if (onLenderSlideOutOpen) {
            onLenderSlideOutOpen();
        } else {
            dispatch(baseDealActionCreators.handleTabChange('lender'));
            dispatch(baseDealAnalyticsActionCreators.lenderTabClicked());
        }
    };

    const handleOfferType = (lenderId: string, lenderName?: string) => {
        if (offerType === FINANCE) {
            dispatch(interactionAnalyticsActionCreators.financeLenderDropdown(vehicleAnalyticsPayload, lenderId, lenderName));
        } else if (offerType === LEASE) {
            dispatch(interactionAnalyticsActionCreators.leaseLenderDropdown(vehicleAnalyticsPayload, lenderId, lenderName));
        }
    };

    const lenderChangeOrigin: LenderChangeOrigin = isSummaryTab ? 'mainScreen' : isInLenderPanel ? 'lenderSlideout' : 'lenderField';

    const handleLenderChange: InterstateOnChangeCallback<SelectInputEventValue> = (event) => {
        const selectedLenderId = event.target.value as string;
        if (!selectedLenderId) return;

        const selectedOption = options.find((opt) => opt.value === selectedLenderId);
        if (selectedOption?.isGroupHeader || selectedOption?.groupHeader) return;

        const parsedValue = findLenderById({ lenderDecisions, lenderId: selectedLenderId });

        if (!parsedValue) return;

        const lenderChangeOptions = { origin: lenderChangeOrigin, isReserveSplitEnabled: isReserveSplitOverrideEnabled };

        if (isEnableLenderAddressF267947 || enableLenderAddress) {
            const validatedValue = {
                ...parsedValue,
                phone: getValidLenderPhone(parsedValue.phone),
                postalCode: getValidLenderPostalCode(parsedValue.postalCode)
            };
            dispatch(lenderActionCreators.updateCreditDecisionLenderWithAddress(validatedValue, lenderChangeOptions));
        } else {
            dispatch(offerReduxActionCreators.updateCreditDecisionLender(parsedValue, lenderChangeOptions));
        }

        if (shouldGroupByDecisionStatus) {
            // Use the lender's official FLD decision to update selected lender's decision status
            const lenderDecision = findLenderDecisionById(allLenderDecisions, selectedLenderId);
            const lenderStatus = isDecisionStatus(lenderDecision?.status) ? lenderDecision.status : '';

            dispatch(offerReduxActionCreators.updateCreditDecisionStatus(lenderStatus));
        }

        dispatch(
            lenderActionCreators.repriceAfterContractingLenderChange({
                previousLender: lender,
                newLender: parsedValue,
                traceIdBefore
            })
        );

        handleOfferType(selectedLenderId, parsedValue.name);
        if (enableLenderAddress) {
            dispatch(fetchLenderAddress(selectedLenderId));
        }
        if (onChangeContractFieldValidation) onChangeContractFieldValidation(parsedValue.name);
    };

    useEffect(() => {
        if (!isPaperLenderEnabled || !isSelectedLenderPaper) return;

        /* istanbul ignore next */
        const input = fieldWrapperRef.current?.querySelector('input[id*="ids-combo-box-input"], input[data-testid*="combo-box"]');
        /* istanbul ignore next */
        if (input) setInputPaddingLeft(getComputedStyle(input).paddingLeft);
    }, [isPaperLenderEnabled, isSelectedLenderPaper, selectedLenderName]);

    const commonProps = {
        size: 'sm' as const,
        id: kebabCase(CREDIT_DECISION_LENDER_NAME_FIELD),
        'data-testid': kebabCase(CREDIT_DECISION_LENDER_NAME_FIELD),
        name: CREDIT_DECISION_LENDER_NAME,
        displayLabel: !isSummaryTab,
        onChange: handleLenderChange,
        value: selectedValue,
        disabled: isLenderDropdownDisabled,
        options: options,
        allowRemoval: false,
        label: lenderLabel,
        hasError: error?.hasError,
        errorMessage: error?.errorMessage
    };

    return (
        <>
            {isSummaryTab && (
                <label
                    className="base-deal-activity-control-custom-label"
                    htmlFor={kebabCase(CREDIT_DECISION_LENDER_NAME_FIELD)}
                    data-testid="credit-decision-label"
                    style={{
                        marginBottom: '0.3rem'
                    }}
                    onClick={handleClick}
                    onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleClick();
                        }
                    }}
                    tabIndex={0}
                    role="button"
                >
                    {isGCDynamicValidationsEnabled ? (
                        <ActionWithErrorBadge entityName={DealTabs.LENDER} xOffset="-2px" yOffset="1px">
                            {lenderLabel}
                        </ActionWithErrorBadge>
                    ) : (
                        lenderLabel
                    )}
                    <ChevronRightIcon fontSize={10} />
                </label>
            )}
            <div ref={fieldWrapperRef} style={{ position: 'relative' }}>
                {isEnableLenderAddressF267947 ? (
                    <DropdownWithDescription
                        {...commonProps}
                        sx={{
                            '& .ids-combo-box-multi-select-input': {
                                padding: 0
                            }
                        }}
                    />
                ) : (
                    <Dropdown
                        {...commonProps}
                        selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
                    />
                )}
                {isPaperLenderEnabled && isSelectedLenderPaper && (
                    <div
                        style={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            overflow: 'hidden',
                            pointerEvents: 'none',
                            left: '10px'
                        }}
                    >
                        <span
                            style={{
                                visibility: 'hidden',
                                whiteSpace: 'pre',
                                overflow: 'hidden',
                                maxWidth: 'calc(99% - 55px)',
                                minWidth: 0,
                                paddingLeft: inputPaddingLeft
                            }}
                        >
                            {selectedLenderName}
                        </span>
                        <DocumentIcon
                            data-testid="credit-decision-lender-paper-icon"
                            fontSize={14}
                            style={{
                                marginLeft: '4px',
                                flexShrink: 0,
                                backgroundColor: isLenderDropdownDisabled
                                    ? theme.tokens.SemColorNeutral100
                                    : theme.palette.background.paper
                            }}
                        />
                    </div>
                )}
            </div>
            {lenderChangeOrigin !== 'lenderSlideout' && <ReserveSplitClearedAlert origin={lenderChangeOrigin} />}
        </>
    );
};

export default CreditDecisionLenderNameField;

function buildAddressDescription({ line1, city, state, postalCode, phone }: LenderAddress) {
    const addressParts: string[] = [];

    if (line1) addressParts.push(line1);
    if (city) addressParts.push(city);
    if (state) addressParts.push(state);
    if (postalCode) addressParts.push(postalCode);

    const addressString = addressParts.join(', ');
    const validPhone = validateLendersListPhoneWithAreaCode(phone) ? phone : null;

    return validPhone ? `${addressString} #${validPhone}` : addressString;
}

/**
 * Helper function to map a lender to an option for the dropdown.
 */
function mapLenderToOption({
    lendersDecision,
    isEnableLenderAddress,
    isGroupedByDecisionStatus,
    allLenderDecisions,
    selectedLenderId,
    selectedLenderName,
    isPaperLenderEnabled
}: {
    lendersDecision: LenderListState;
    isEnableLenderAddress: boolean;
    isGroupedByDecisionStatus: boolean;
    allLenderDecisions: FldLenderDecision[];
    selectedLenderId?: string;
    selectedLenderName?: string;
    isPaperLenderEnabled: boolean;
}): DropdownOptionWithDescription[] {
    const contractTypeByLenderId = new Map(lendersDecision.map((lender) => [lender.lenderId, lender.contractType]));
    const hasIcon = (lenderId: string) => isPaperLenderEnabled && contractTypeByLenderId.get(lenderId) === ContractType.Paper;

    if (!isGroupedByDecisionStatus) {
        return [...lendersDecision]
            .sort((a, b) => a.lenderName.localeCompare(b.lenderName))
            .map(({ lenderId, lenderName, line1, city, state, postalCode, phone }) => {
                const description = isEnableLenderAddress
                    ? buildAddressDescription({
                          line1,
                          city,
                          state,
                          postalCode,
                          phone
                      })
                    : undefined;

                return {
                    value: lenderId,
                    label: lenderName,
                    description,
                    hasIcon: hasIcon(lenderId)
                };
            });
    }

    const selectedLender = lendersDecision.find((lender) => {
        const matchesSelectedId = !!selectedLenderId && lender.lenderId === selectedLenderId;
        const matchesSelectedName = !!selectedLenderName && lender.lenderName === selectedLenderName;
        return matchesSelectedId || matchesSelectedName;
    });

    const selectableLenders = lendersDecision.filter((lender) => {
        const matchesSelectedId = !!selectedLenderId && lender.lenderId === selectedLenderId;
        const matchesSelectedName = !!selectedLenderName && lender.lenderName === selectedLenderName;
        return !matchesSelectedId && !matchesSelectedName;
    });

    const groups = groupLendersByDecisionStatus(selectableLenders, allLenderDecisions);
    const options: DropdownOptionWithDescription[] = [];

    if (selectedLender) {
        const description = isEnableLenderAddress
            ? buildAddressDescription({
                  line1: selectedLender.line1,
                  city: selectedLender.city,
                  state: selectedLender.state,
                  postalCode: selectedLender.postalCode,
                  phone: selectedLender.phone
              })
            : undefined;

        options.push({
            value: selectedLender.lenderId,
            label: selectedLender.lenderName,
            description,
            hasIcon: hasIcon(selectedLender.lenderId)
        });
    }

    const decisionStatusGroups = [
        { label: 'Approved', lenders: groups.approved },
        { label: 'Counter Offer', lenders: groups.counterOffer },
        { label: 'Declined', lenders: groups.declined },
        { label: 'Other Lenders', lenders: groups.other }
    ];

    decisionStatusGroups.forEach(({ label, lenders }) => {
        if (lenders.length > 0) {
            options.push({
                value: `header-${label.toLowerCase().replace(/\s+/g, '-')}`,
                label,
                isGroupHeader: true,
                groupHeader: true
            });

            lenders.forEach(({ lenderId, lenderName, line1, city, state, postalCode, phone }) => {
                const description = isEnableLenderAddress
                    ? buildAddressDescription({
                          line1,
                          city,
                          state,
                          postalCode,
                          phone
                      })
                    : undefined;

                options.push({
                    value: lenderId,
                    label: lenderName,
                    description,
                    hasIcon: hasIcon(lenderId)
                });
            });
        }
    });

    return options;
}

/**
 * Helper function to find a lender by id from lenderDecisions.
 */
function findLenderById({
    lenderDecisions,
    lenderId
}: {
    lenderDecisions: LenderListState;
    lenderId: string;
}): SelectedLenderWithAddress | undefined {
    const lenderDecision = lenderDecisions.find((l) => l.lenderId === lenderId);
    if (!lenderDecision) return undefined;

    const { lenderId: id, lenderName: name, ...rest } = lenderDecision;
    return { id, name, ...rest };
}
